import type { BankMovement, LedgerAllocation } from "../../types/accounting.types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EditableMovementState = {
  date: string
  depositDate: string
  issueDate: string
  label: string
  accountId: string
  paymentMode: string
  remittanceNumber: string
  paymentNumber: string
  debit: string
  credit: string
};

export type MovementAttachmentState = {
  url: string
  fileName: string | null
  fileType: string | null
};

export type EditableAllocationState = {
  id: string
  date: string
  budgetType: string
  operationTypeId: string
  operationType: string
  description: string
  debit: string
  credit: string
  stockDebitQuantity: string
  stockCreditQuantity: string
};

export type AllocationCategory = "ticketing_subsidy" | "activity_module" | "reimbursement" | "ecommerce" | "catalog_booking" | "simple_entry" | "stock" | "legacy_purchase" | "legacy_travel" | "legacy_reimbursement" | "legacy_rental";
export type RuleConditionKind = "keyword" | "amount" | "bank_account";
export type RuleTextOperator = "contains" | "equals" | "starts_with" | "ends_with";
export type RuleAmountOperator = "eq" | "neq" | "lt" | "lte" | "gte" | "gt" | "between";

export type LocalRuleCondition = {
  id: string
  kind: RuleConditionKind
  textOperator: RuleTextOperator
  textValue: string
  amountOperator: RuleAmountOperator
  amountValue: string
  amountValueTo: string
  absoluteValue: boolean
  bankAccountId: string
};

export type LocalCategorizationRule = {
  id: string
  name: string
  conditions: LocalRuleCondition[]
  budgetType: string
  operationTypeId: string
  operationTypeName: string
  isActive: boolean
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const RULE_TEXT_OPERATOR_OPTIONS: Array<{ value: RuleTextOperator, label: string }> = [
  { value: "contains", label: "Contient" },
  { value: "equals", label: "Est" },
  { value: "starts_with", label: "Commence par" },
  { value: "ends_with", label: "Finit par" },
];

export const RULE_AMOUNT_OPERATOR_OPTIONS: Array<{ value: RuleAmountOperator, label: string }> = [
  { value: "eq", label: "= égal à ..." },
  { value: "neq", label: "≠ Différent de ..." },
  { value: "lt", label: "< inférieur à ..." },
  { value: "lte", label: "≤ inférieur ou égal à ..." },
  { value: "gte", label: "≥ supérieur ou égal à ..." },
  { value: "gt", label: "> supérieur à ..." },
  { value: "between", label: "Compris entre ... et ..." },
];

export const CELL_EDITABLE_INPUT_CLASS = [
  "h-7 w-full !text-[12px] md:!text-[12px] px-2 rounded-none border border-transparent bg-transparent shadow-none",
  "focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-violet-300 focus-visible:bg-violet-50/40",
  "hover:border-slate-200",
].join(" ");

export const LEGACY_PAYMENT_MODES = [
  "Chèque",
  "Prélèvement",
  "Virement",
  "Carte",
  "Remise de prélèvements",
  "Remise de chèques",
  "Remise de cartes",
  "Remise de virements",
  "Remise de virements scindée",
  "Remise d'espèces",
  "Remise autre",
  "Autre",
];

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

export const formatAmount = (value: number) => value.toLocaleString("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const parseNullableNumber = (value: string): number | null => {
  if (!value.trim()) return null;

  const normalized = value
    .replace(/[€\s\u202F]/g, "")
    .replace(",", ".");
  const numberValue = Number(normalized);

  if (Number.isNaN(numberValue)) {
    return null;
  }

  return numberValue;
};

export const formatCurrencyField = (value: string): string => {
  const parsed = parseNullableNumber(value);
  if (parsed === null) return "";
  return formatAmount(parsed);
};

/**
 * Une ligne comptable est soit au débit, soit au crédit : saisir les deux
 * produit un montant incohérent (et un sens de mouvement indécidable côté
 * compta). Dès qu'un côté porte un montant non nul, l'autre est verrouillé - il
 * faut vider le premier pour changer de sens.
 *
 * Un montant nul ne verrouille rien, et une ligne qui porterait déjà les deux
 * (données antérieures à cette garde) reste modifiable des deux côtés, sans
 * quoi elle deviendrait impossible à corriger.
 */
export const getAmountSideLock = (
  debit: string | null | undefined,
  credit: string | null | undefined,
) => {
  const hasDebit = (parseNullableNumber(debit ?? "") ?? 0) !== 0;
  const hasCredit = (parseNullableNumber(credit ?? "") ?? 0) !== 0;

  return {
    isDebitLocked: hasCredit && !hasDebit,
    isCreditLocked: hasDebit && !hasCredit,
  };
};

export type OperationTypeNature = "expense" | "revenue";
export type OperationTypeNatureFilter = "all" | OperationTypeNature;

/** Un débit appelle une catégorie de dépense, un crédit une recette ; rien si le sens est indécidable. */
export const getPreferredOperationTypeNature = (
  debit: string | null | undefined,
  credit: string | null | undefined,
): OperationTypeNature | null => {
  const hasDebit = (parseNullableNumber(debit ?? "") ?? 0) !== 0;
  const hasCredit = (parseNullableNumber(credit ?? "") ?? 0) !== 0;
  if (hasDebit && !hasCredit) return "expense";
  if (hasCredit && !hasDebit) return "revenue";
  return null;
};

/**
 * Regroupe les catégories par nature, la nature suggérée par l'écriture en tête.
 * Sans nature, une catégorie compte comme une dépense (même règle que la page Budgets).
 */
export const groupOperationTypesByNature = <T extends { nature?: OperationTypeNature }>(
  operationTypes: T[],
  preferredNature: OperationTypeNature | null,
  filter: OperationTypeNatureFilter,
): Array<{ nature: OperationTypeNature, operationTypes: T[] }> => {
  const order: OperationTypeNature[] = preferredNature === "revenue" ? ["revenue", "expense"] : ["expense", "revenue"];
  return order
    .filter((nature) => filter === "all" || filter === nature)
    .map((nature) => ({
      nature,
      operationTypes: operationTypes.filter((operationType) => (operationType.nature ?? "expense") === nature),
    }))
    .filter((group) => group.operationTypes.length > 0);
};

/**
 * Un champ verrouillé par la règle débit/crédit reste focusable et lisible
 * (readOnly + aria-disabled) : `disabled` le sortirait du parcours clavier avec
 * son explication, et lui retirerait le focus en pleine frappe. Cette classe lui
 * donne l'état visuel que `disabled:` fournissait gratuitement.
 */
export const LOCKED_AMOUNT_INPUT_CLASS = "bg-muted/60 text-muted-foreground cursor-not-allowed";

/**
 * Ce qu'une écriture peut porter au maximum sur son opération, dans le repère de
 * l'opération : positif, c'est un crédit ; négatif, un débit de sa valeur absolue.
 *
 * POURQUOI UNE ADDITION, ET POURQUOI CE N'EST PAS UN CALCUL DE BORNE.
 *
 * `operationBalance` est le `solde_affectation` servi par le back — la colonne
 * même que borne son garde de sur-affectation. Dans un éditeur, ce solde comprend
 * la ligne qu'on est en train de modifier : lui rendre sa propre contribution
 * donne ce qui reste disponible POUR ELLE. Sans ça, un élu qui édite une ligne de
 * 75,00 € sur une opération soldée lirait « reste 0,00 € » et conclurait qu'il ne
 * peut plus rien changer, quand la baisser est justement permis.
 *
 * L'intervalle [0, credit − debit] n'est JAMAIS recalculé ici, et rien dans le
 * front ne juge la saisie : le garde back reste seul à trancher. Ne pas
 * transformer cette addition en contrôle de validité — ce serait une troisième
 * définition des bornes, à côté du modèle et du contrôle d'intégrité, et c'est
 * exactement le défaut qu'une constante recopiée a déjà coûté côté API.
 */
export const getAllocationRoom = (
  operationBalance: number | null | undefined,
  allocation: { debit: number | null, credit: number | null } | null | undefined,
): number => (operationBalance ?? 0) + ((allocation?.credit ?? 0) - (allocation?.debit ?? 0));

export const DEBIT_LOCKED_HINT = "Un crédit est déjà saisi : videz-le pour saisir un débit.";
export const CREDIT_LOCKED_HINT = "Un débit est déjà saisi : videz-le pour saisir un crédit.";
export const DEBIT_UNITS_LOCKED_HINT = "Des unités créditées sont déjà saisies : videz-les pour saisir un débit.";
export const CREDIT_UNITS_LOCKED_HINT = "Des unités débitées sont déjà saisies : videz-les pour saisir un crédit.";

export const normalizeCurrencyTyping = (value: string): string => {
  const sanitized = value
    .replace(/[€\s\u202F]/g, "")
    .replace(/\./g, ",")
    .replace(/[^0-9,]/g, "");

  const [rawInteger, ...rawDecimals] = sanitized.split(",");
  let integerPart = rawInteger.replace(/\D/g, "");
  integerPart = integerPart.replace(/^0+(?=\d)/, "");
  if (!integerPart) integerPart = "0";

  const decimalPart = rawDecimals.join("").replace(/\D/g, "").slice(0, 2);
  if (sanitized.includes(",")) return `${integerPart},${decimalPart}`;
  return integerPart;
};

export const getSourceLabel = (source: string | null) =>
  source?.toLowerCase().includes("bridge") ? "Bridge" : "Saisie manuelle";

export const normalizeRef = (value: string | null | undefined): string | null => {
  const ref = value?.trim();
  if (!ref) return null;
  if (/^[.\-–—\s]+$/.test(ref)) return null;
  return ref;
};

export const normalizeComparable = (value: string | null | undefined): string => {
  if (!value) return "";
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
};

export const normalizeSearchText = (value: string | null | undefined): string => {
  if (!value) return "";
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

export const fileTypeFromMime = (mimeType?: string | null): string => {
  if (!mimeType) return "AUTRE";
  if (mimeType === "application/pdf") return "PDF";
  if (mimeType.startsWith("image/")) return "IMAGE";
  return "AUTRE";
};

export const extractFileNameFromAttachmentUrl = (url?: string | null): string | null => {
  if (!url) return null;
  try {
    const parsedUrl = new URL(url);
    const contentDisposition = parsedUrl.searchParams.get("response-content-disposition")
      || parsedUrl.searchParams.get("content-disposition")
      || "";

    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]).trim() || null;
    }

    const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
    if (quotedMatch?.[1]) {
      return decodeURIComponent(quotedMatch[1]).trim() || null;
    }

    const lastSegment = parsedUrl.pathname.split("/").filter(Boolean).pop();
    return lastSegment ? decodeURIComponent(lastSegment) : null;
  }
  catch {
    return null;
  }
};

export const isImageAttachment = (fileType?: string | null, url?: string | null): boolean => {
  const normalizedType = (fileType || "").toUpperCase();
  if (normalizedType === "IMAGE") return true;
  if (normalizedType === "PDF") return false;
  if (!url) return false;
  try {
    const parsedUrl = new URL(url);
    const responseContentType = (parsedUrl.searchParams.get("response-content-type") || "").toLowerCase();
    if (responseContentType.startsWith("image/")) return true;
    if (responseContentType === "application/pdf") return false;
  }
  catch {
    return false;
  }
  return false;
};

export const getAllocationCategory = (allocation: LedgerAllocation): AllocationCategory => {
  if ((allocation.budgetType ?? "").toUpperCase() === "STOCK") return "stock";
  if (allocation.orderId) return "ticketing_subsidy";
  // v2 catalog booking (module Location) — détecté par catalogBookingId. Doit
  // passer AVANT les heuristiques legacy : la `categorie` côté AO valait
  // « Locations », qui matchait legacy_rental. Elle vaut « Réservation »
  // depuis le 31/08, mais le garde reste utile pour les lignes d'avant.
  if (allocation.activityOperationId && allocation.catalogBookingId) return "catalog_booking";
  // v2 reimbursement: has refundId (via activite_operation.remboursement_id)
  if (allocation.activityOperationId && allocation.refundId && /remboursement/i.test(allocation.activityOperationCategory ?? "")) return "reimbursement";
  // Legacy categories: activityOperationId present but NO refundId/ecommerceOrderId/catalogBookingId
  if (allocation.activityOperationId && !allocation.ecommerceOrderId && !allocation.refundId && !allocation.catalogBookingId) {
    const cat = (allocation.activityOperationCategory ?? "").toLowerCase();
    if (cat === "achats stockés") return "legacy_purchase";
    if (cat === "voyages & loisirs") return "legacy_travel";
    if (cat === "remboursements") return "legacy_reimbursement";
    if (cat === "locations") return "legacy_rental";
  }
  // v2 ecommerce: has ecommerceOrderId
  if (allocation.activityOperationId && allocation.ecommerceOrderId) return "ecommerce";
  if (allocation.activityOperationId) return "activity_module";
  return "simple_entry";
};

export const getAllocationCategoryEmoji = (category: AllocationCategory): string => {
  switch (category) {
    case "ticketing_subsidy":
      return "🧑‍💻";
    case "activity_module":
      return "🏋️‍♀️";
    case "reimbursement":
      return "💰";
    case "ecommerce":
      return "🛒";
    case "catalog_booking":
      return "🔑";
    case "stock":
      return "📦";
    case "legacy_purchase":
      return "🏷️";
    case "legacy_travel":
      return "✈️";
    case "legacy_reimbursement":
      return "💸";
    case "legacy_rental":
      return "🏠";
    default:
      return "🧾";
  }
};

export const getAllocationCategoryLabel = (category: AllocationCategory): string => {
  switch (category) {
    case "ticketing_subsidy":
      return "Subvention billetterie";
    case "activity_module":
      return "Bon plan";
    case "reimbursement":
      return "Remboursement";
    case "ecommerce":
      return "Bons plans";
    case "catalog_booking":
      return "Catalogue";
    case "stock":
      return "Stock";
    case "legacy_purchase":
      return "Achats stockés (legacy)";
    case "legacy_travel":
      return "Voyages & Loisirs (legacy)";
    case "legacy_reimbursement":
      return "Remboursement v1 (legacy)";
    case "legacy_rental":
      return "Locations (legacy)";
    default:
      return "Écriture simple";
  }
};

export const areNumbersEqual = (left: number | null, right: number | null): boolean => {
  if (left === null && right === null) return true;
  if (left === null || right === null) return false;
  return Math.abs(left - right) < 0.00001;
};

/**
 * Le serveur a répondu et a refusé la requête (validation, droits, conflit) :
 * rien n'a été enregistré, l'écran doit revenir à la dernière version connue de
 * la base.
 *
 * Volontairement limité aux 4xx. Une panne réseau ne dit rien de l'état du
 * serveur, et une 5xx encore moins : un 502 derrière un écrit déjà commité
 * ferait réafficher l'ancienne valeur alors que la nouvelle est en base, et le
 * comptable ressaisirait par-dessus. Dans ces deux cas la saisie reste à
 * l'écran, prête à être réessayée.
 */
export const isServerRejection = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const status = (error as { response?: { status?: unknown } }).response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
};

export const extractApiErrorMessage = (error: unknown): string => {
  const fallback = "Erreur lors de l'enregistrement de l'écriture";
  if (!error || typeof error !== "object") return fallback;

  const maybeAxiosError = error as {
    response?: {
      data?: {
        errors?: unknown
        details?: { base?: Array<{ error?: string }> }
        error?: unknown
        message?: string
      }
    }
    message?: string
  };

  const responseData = maybeAxiosError.response?.data;

  if (Array.isArray(responseData?.errors) && typeof responseData.errors[0] === "string") {
    return responseData.errors[0];
  }

  const baseError = responseData?.details?.base?.[0]?.error;
  if (typeof baseError === "string" && baseError.trim()) {
    return baseError;
  }

  // Plusieurs endpoints compta répondent `{ "error": "..." }` au lieu de
  // `{ "errors": [...] }` ; sans cette clé le comptable ne lit que « Request
  // failed with status code 422 ».
  // Volontairement pas délégué à extractApiErrorDetail (shared/utils/
  // errorHandling) : celui-ci exige un vrai AxiosError et donne la priorité à
  // `message`, là où les appelants d'ici passent aussi des objets nus et
  // attendent `errors[0]` d'abord.
  if (typeof responseData?.error === "string" && responseData.error.trim()) {
    return responseData.error;
  }

  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message;
  }

  if (typeof maybeAxiosError.message === "string" && maybeAxiosError.message.trim()) {
    return maybeAxiosError.message;
  }

  return fallback;
};

const formatQuantity = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

export const toEditableState = (allocation: LedgerAllocation): EditableAllocationState => ({
  id: allocation.id,
  date: allocation.date ?? "",
  budgetType: allocation.budgetType ?? "",
  operationTypeId: allocation.operationTypeId ?? "",
  operationType: allocation.operationTypeName ?? allocation.operationType ?? "",
  description: allocation.description ?? "",
  debit: allocation.debit !== null && allocation.debit !== undefined ? formatAmount(allocation.debit) : "",
  credit: allocation.credit !== null && allocation.credit !== undefined ? formatAmount(allocation.credit) : "",
  stockDebitQuantity: formatQuantity(allocation.stockDebitQuantity),
  stockCreditQuantity: formatQuantity(allocation.stockCreditQuantity),
});

// ADM-721/ADM-722 : une affectation créée pendant la session du drawer n'est
// nettoyée à la fermeture que si elle est restée identique à sa création (=
// l'utilisateur l'a ajoutée puis n'a rien saisi). La comparaison DOIT inclure
// les quantités stock : une écriture stock ne touche ni le débit ni le crédit
// monétaires, donc sans elles une écriture stock valide est vue « inchangée »
// puis supprimée — d'où la ligne stock qui disparaît après enregistrement et
// l'historique absent du Grand Livre des stocks.
export const isSessionAllocationUnchanged = (
  current: EditableAllocationState,
  snapshot: EditableAllocationState,
): boolean =>
  current.date === snapshot.date
  && current.budgetType === snapshot.budgetType
  && current.operationTypeId === snapshot.operationTypeId
  && current.operationType === snapshot.operationType
  && current.description === snapshot.description
  && current.debit === snapshot.debit
  && current.credit === snapshot.credit
  && current.stockDebitQuantity === snapshot.stockDebitQuantity
  && current.stockCreditQuantity === snapshot.stockCreditQuantity;

/**
 * Décide si une affectation créée pendant la session du drawer doit être
 * supprimée à sa fermeture.
 *
 * Cas courant : on compare le brouillon local au snapshot de création, pour ne
 * jamais supprimer une ligne que l'utilisateur vient de remplir.
 *
 * Enregistrement REFUSÉ par le serveur : on garde la ligne. Le brouillon a été
 * jeté pour réafficher l'état réellement stocké, la ligne paraît donc « vide »
 * au nettoyage alors que l'utilisateur venait de la remplir — la supprimer lui
 * ferait perdre la ligne en plus de sa saisie, et une écriture simple porte de
 * toute façon le montant prérempli du mouvement, qui vaut d'être retenté.
 *
 * UNE exception, l'écriture STOCK : elle ne préremplit rien (débit et crédit
 * forcés à 0 à la création, référence et quantités nulles) et sa saisie vit dans
 * l'état local du tiroir stock, perdu à la fermeture. Refusée ET restée telle
 * qu'à sa création, elle ne porte donc rien à retenter : la garder laisse en base
 * une écriture qui ne s'impute sur aucun stock tout en pesant sur le solde de
 * stock de son opération (contrôle d'intégrité `cma-stock-sans-porteur`). Une
 * écriture stock refusée sur une seconde tentative, elle, diffère de son snapshot
 * et reste épargnée.
 */
export const shouldCleanupSessionAllocation = ({
  persisted,
  draft,
  snapshot,
  wasRejected,
}: {
  persisted: EditableAllocationState
  draft: EditableAllocationState | undefined
  snapshot: EditableAllocationState
  wasRejected: boolean
}): boolean => {
  if (wasRejected) {
    return persisted.budgetType === "STOCK" && isSessionAllocationUnchanged(persisted, snapshot);
  }
  return isSessionAllocationUnchanged(draft ?? persisted, snapshot);
};

export const toEditableMovementState = (movement: BankMovement): EditableMovementState => ({
  date: movement.date ?? "",
  depositDate: movement.depositDate ?? "",
  issueDate: movement.issueDate ?? "",
  label: movement.label ?? "",
  accountId: movement.accountId ?? "",
  paymentMode: movement.paymentMode ?? "",
  remittanceNumber: movement.remittanceNumber ?? "",
  paymentNumber: movement.paymentNumber ?? "",
  debit: movement.debit !== null && movement.debit !== undefined ? formatAmount(movement.debit) : "",
  credit: movement.credit !== null && movement.credit !== undefined ? formatAmount(movement.credit) : "",
});

export const resolveBudgetType = (budgetType?: string | null): "ASC" | "AEP" => {
  return budgetType?.trim().toUpperCase() === "AEP" ? "AEP" : "ASC";
};

export const makeEmptyRuleCondition = (suffix?: string): LocalRuleCondition => ({
  id: `cond-${Date.now()}${suffix ? `-${suffix}` : ""}`,
  kind: "keyword",
  textOperator: "contains",
  textValue: "",
  amountOperator: "eq",
  amountValue: "",
  amountValueTo: "",
  absoluteValue: true,
  bankAccountId: "",
});
